import {CustomerGroupPrice} from "../../../services/productGroupService";
import {useDispatch, useSelector} from "react-redux";
import {IGlobalProduct, IState} from "../../../types/state";
import React, {FC, Fragment, MouseEvent as ReactMouseEvent, ReactNode, useEffect, useRef, useState} from "react";
import {useToast} from "../../../atoms/Toast/useToast";
import {useRouter} from "next/router";
import cls from "./customInnerPage.module.scss";
import {CheckToastSvg, FailSvg, HeartActive, HeartSvg} from "../../../svg";
import {wishlistAddItem, wishlistRemoveItem} from "../../../store/wishlist";
import Script from "next/script";
import {IProduct, ISizeChart, TranslationsType} from "../../../types/homePageTypes";
import CustomDetails from "./CustomDetails";
import {ICurrencies} from "../Wrappers/MainLayout";
import CustomDownloadableSideBar from "./CustomDownloadableSideBar";
import CustomSocialLinks from "./CustomSocialLinks";
import CustomDetailTabButtons from "./CustomDetailTabButtons";
import CustomInfoPart from "./CustomInfoPart";
import {
    addCartDivStyleToggleSimple,
    handleCardClick,
    isSelectedDetailSectionClass,
    openedBoxStyles,
    schemaProduct,
    showBoxAboutDetails,
    useFetchCartToken
} from "./helpers";
import CustomProductGallery from "./CustomProductGallery";
import {IFormComponentType} from "../../builder/types";
import {IRequestFormDetails} from "../../../pages/[productSlug]";

interface ICustomProduct {
    dbName: string;
    domain: string;
    productSlug: string;
    selectedRate: ICurrencies;
    product: IProduct;
    backOrderValue: string;
    translationsData: TranslationsType;
    selectedLocale: string;
    schemaBrandName: string;
    showDetailsInFront: boolean;
    showBuilderComponents: (id: number) => Record<"true" | "false", ReactNode>;
    showBuilderData: boolean;
    requestFormInfo: IFormComponentType | null;
    requestFormDetails: IRequestFormDetails
    isOhmTheme: boolean;
    sizeChart: ISizeChart | null
}

const CustomDownloadableProduct: FC<ICustomProduct> = (
    {
        dbName,
        productSlug,
        domain,
        selectedRate,
        product,
        backOrderValue,
        translationsData,
        selectedLocale,
        schemaBrandName,
        showDetailsInFront,
        showBuilderComponents,
        showBuilderData,
        requestFormInfo,
        requestFormDetails,
        isOhmTheme,
        sizeChart
    }
) => {
    const [checkedLinks, setCheckedLinks] = useState<{ id: number }[]>([]);
    const [clientSideProduct, setClientSideProduct] = useState<IProduct>(product);
    useEffect(() => {
        setClientSideProduct(product)
    }, [productSlug, selectedLocale]);

    const {
        flats: [{
            name: productName = "",
            description: productDesc = "",
            min_price: productMinPrice = null,
            special_price: productSpecialPrice = null,
            short_description: productShortDesc = "",
            price: productPrice = null,
            price_html: productPriceHtml = null,
            has_up_sell = 0,
            has_cross_sell = 0,
        } = {}] = [],
        images: [productImgPath = ""] = [],
        id: productId,
        sku: productSku = "",
        details: productDetails = [],
        sku_option: {name: productSkuOptionName = "SKU"} = {},
        short_description_option: {name: productShortDescOptionName = ""} = {},
        description_option: {name: productDescOptionName = ""} = {},
        product_options = [],
        qty: [productInitialQty]
    } = clientSideProduct


    const [isSelectDetail, setIsSelectDetail] = useState<boolean>(productDetails.length > 6);
    const cartToken = useSelector((state: IState) => state.cartToken);
    const {token: customerToken} = useSelector((state: IState) => state.customer);
    const signed = useSelector((state: IState) => state.customer.authenticated);
    const wishlist = useSelector((state: IState) => state.wishlist);
    const isProductInWishList = wishlist?.find((item: IGlobalProduct) => item.id === productId)
    const [wishIcon, setWishIcon] = useState<React.JSX.Element>(<HeartSvg/>)
    useEffect(() => {
        setWishIcon(!!isProductInWishList ? <HeartActive className={cls["inner-wishlist"]}/> : <HeartSvg/>)
    }, [isProductInWishList]);
    const dispatch = useDispatch();
    const toast = useToast();
    const {asPath} = useRouter();

    const toastOpenHandle: Record<string, () => void> = {
        "true": () => toast.open(
            <span className="d-flex chek-fms">
            <FailSvg />
                {translationsData.ProductNotAvailable}
        </span>
        ),
        "false": () => toast.open(
            <span className="d-flex chek-fms">
            <CheckToastSvg />
                {translationsData.ToastAddToCart}
        </span>
        ),
    };

    const addToast = (error?: boolean) => toastOpenHandle[`${error}`]()

    const schema = schemaProduct(asPath, productName, productDesc, productImgPath, productSku, productId, dbName, domain, schemaBrandName)

    useFetchCartToken(cartToken)

    const selectTab = (e: ReactMouseEvent<HTMLDivElement>) => {
        setIsSelectDetail((e.target as HTMLDivElement).id === "detail")
    }

    const handleWishIconClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        e.preventDefault();
        if (!signed) {
            toast.open(
                <span className="d-flex faild-toast-fms">
                    <FailSvg/>
                    {translationsData.ToastSignIn}
                </span>
            )
        } else {
            !!isProductInWishList ?
                dispatch(wishlistRemoveItem(product.id))
                : dispatch(wishlistAddItem(product, selectedLocale))
            const toastChildren = {
                "true": {svg: <FailSvg/>, title: translationsData?.ToastWishListRemove},
                "false": {svg: <CheckToastSvg/>, title: translationsData?.ToastWishList}
            }
            toast.open(
                <span className="d-flex faild-toast-fms">
                    {toastChildren[`${!!isProductInWishList}`].svg}
                    {toastChildren[`${!!isProductInWishList}`].title}
                </span>
            )
        }
    };

    const priceHtmlAvailable = {
        "true": product?.flats[0]?.price_html?.map((e: string, i: number) => (
            <span key={i} className={cls["product_price_html"]}>{e}</span>
        )),
        "false": <></>
    };

    const isButtonDisabled = {
        "true": !(!!+backOrderValue),
        "false": false
    };

    const descriptions = (
        <div className={cls["product-inner-long-description"]}
        >
            <div style={{paddingLeft: "30px"}}>
                <div dangerouslySetInnerHTML={{__html: productDesc}}/>
            </div>
        </div>
    );

    const details = <CustomDetails
        details={productDetails}
        isSelectDetail={isSelectDetail}
        showDetailsInFront={showDetailsInFront}
        translationsData={translationsData}
        dbName={dbName}
        domain={domain}
    />

    return (
        <Fragment>
            <Script
                id={schema.name}
                type="application/ld+json"
                dangerouslySetInnerHTML={{__html: JSON.stringify(schema)}}
                defer
            />
            <div>
                {showBuilderComponents(1)[`${showBuilderData}`]}
                <div className={cls["product__content"]}>
                    <CustomProductGallery images={product.images} dbName={dbName}/>
                    <div className={cls.product__info}>
                        <CustomInfoPart
                            product={clientSideProduct}
                            productName={productName}
                            productSkuOptionName={productSkuOptionName}
                            productSku={productSku}
                            selectedCurrSymbol={selectedRate?.symbol}
                            selectedCurrCode={selectedRate?.code}
                            clientSidePrice={{
                                price: clientSideProduct.flats[0].price,
                                specialPrice: clientSideProduct.flats[0].special_price || 0,
                                cost: clientSideProduct.flats[0].cost
                            }}
                            priceHtmlAvailable={priceHtmlAvailable[`${!!productPriceHtml}`]}
                            productShortDescOptionName={productShortDescOptionName}
                            productShortDesc={productShortDesc}
                            productOptions={product_options}
                            children={<Fragment />}
                            translationsData={translationsData}
                            isOhmTheme={isOhmTheme}
                            sizeChart={sizeChart}
                            domain={domain}
                            dbName={dbName}
                        />
                        <CustomDownloadableSideBar
                            product={product}
                            ProductAddToCart_INTL={translationsData?.AddToCartButtonTitle}
                            actionClassName={addCartDivStyleToggleSimple[`${true}`]}
                            handleCardClick={handleCardClick(
                                {
                                    dispatch,
                                    productId,
                                    selectedRate,
                                    has_up_sell,
                                    has_cross_sell,
                                    addToast,
                                    selectedLocale,
                                    cartToken,
                                    customerToken,
                                    product,
                                    quantity: 1,
                                    links: checkedLinks.map(({ id }) => id)
                                }
                            )[`${true}`][`${!!parseFloat(has_up_sell)}`][`${!!parseFloat(has_cross_sell)}`]}
                            isButtonDisabled={isButtonDisabled[`${productInitialQty === 0}`]}
                            wishListButtonClick={handleWishIconClick}
                            wishIconClicked={wishIcon}
                            dbName={dbName}
                            checkedLinks={checkedLinks}
                            setCheckedLinks={setCheckedLinks}
                            selectedCurrSymbol={selectedRate?.symbol}
                            selectedCurrCode={selectedRate?.code}
                            translationsData={translationsData}
                            requestFormDetails={requestFormDetails}
                            requestFormInfo={requestFormInfo}
                            isOhmTheme={isOhmTheme}
                        />
                        <CustomSocialLinks dbName={dbName} domain={domain} productSlug={productSlug}/>
                    </div>
                </div>
                {showBuilderComponents(2)[`${showBuilderData}`]}
                <CustomDetailTabButtons
                    boxAboutDetailsClass={showBoxAboutDetails[`${showDetailsInFront}`]}
                    isSelectedDetailSectionClass={isSelectedDetailSectionClass(descriptions, details)[`${isSelectDetail}`]}
                    productDescOptionName={productDescOptionName}
                    Details_INTL={translationsData?.Details}
                    selectTab={selectTab}
                    showDescription={!!productDesc}
                    showDetailsInFront={showDetailsInFront}
                />
                {isSelectedDetailSectionClass(descriptions, details)[`${isSelectDetail}`].content}
                {showBuilderComponents(3)[`${showBuilderData}`]}
            </div>
        </Fragment>
    );
};

export default CustomDownloadableProduct;
